import React from 'react';

import { get } from '@lumapps/constants';
import { useFilePicker } from '@lumapps/lumx-files/hooks/useFilePicker';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { Image } from '@lumapps/lumx-images/types';
import { mdiImageFilter } from '@lumapps/lumx/icons';
import { useMediaPickerDropdown } from '@lumapps/medias/hooks/useMediaPickerDropdown';
import { useResponsive } from '@lumapps/responsive';
import { useTranslate } from '@lumapps/translations';
import { UseInsertEnhancedImageButtonProps } from '@lumapps/wrex-enhanced-image/components/buttons/InsertEnhancedImageButton/useInsertEnhancedImageButton';
import { ImageEditor } from '@lumapps/wrex-enhanced-image/types';
import { ReactEditor, useSlateStatic } from '@lumapps/wrex/slate';
import { ToolbarItem } from '@lumapps/wrex/types';

import { WREX_IMAGE_GALLERY } from '../../../keys';
import { formatFilesToImageStructure } from '../../../utils/formatFilesToImageStructure';
import { ImageGalleryDialog } from '../../ImageGalleryDialog';

type UseInsertImageGalleryButtonProps = Omit<UseInsertEnhancedImageButtonProps, 'onQuickUpload' | 'scope'>;

const { isMobileAppWebView } = get();

export const useInsertImageGalleryButton = ({
    currentFiles,
    isDriveAllowed,
    isLumdriveAllowed,
    isInCommunityContext,
    mediaPickerParams,
    onMediaChange,
    otherProps,
}: UseInsertImageGalleryButtonProps) => {
    const { translateKey } = useTranslate();
    const { isSmall } = useResponsive();
    const editorStatic = useSlateStatic() as ReactEditor & ImageEditor;
    const [isImageGalleryDialogOpen, openImageGalleryDialog, closeImageGalleryDialog] = useBooleanState(false);
    const [pickedImages, setPickedImages] = React.useState<Image[]>([]);
    const shouldHideButton = isMobileAppWebView || isSmall;
    const { hiddenInput, openPicker } = useFilePicker({
        onSelectFiles: (files) => {
            if (files) {
                const images = formatFilesToImageStructure(files, true);
                setPickedImages(images);
                openImageGalleryDialog();
            }
        },
        accept: 'image/*',
        multiple: true,
        inputOrigin: 'image-gallery-insertion-button',
    });

    const handleOnSave = async (images: Image[]) => {
        await editorStatic.insertImageGallery(images);
        closeImageGalleryDialog();
    };

    const { mediaPickerDropdownProps } = useMediaPickerDropdown({
        onUploadFromComputer: openPicker,
        currentFiles,
        isDriveAllowed,
        isInCommunityContext,
        isLumdriveAllowed,
        mediaPickerParams,
        onMediaChange,
        subMenuProps: {
            itemKey: 'image-gallery',
            icon: mdiImageFilter,
            otherProps,
            verticalModeProps: {
                label: translateKey(WREX_IMAGE_GALLERY.BUTTON_LABEL),
                tooltipLabel: undefined,
            },
            helper: translateKey(WREX_IMAGE_GALLERY.BUTTON_HELPER),
        },
    });

    const insertImageGalleryButtonProps: ToolbarItem = mediaPickerDropdownProps || {
        type: 'option',
        itemKey: 'image-gallery',
        icon: mdiImageFilter,
        onClick: openPicker,
        otherProps,
        label: translateKey(WREX_IMAGE_GALLERY.BUTTON_LABEL),
        tooltipLabel: translateKey(WREX_IMAGE_GALLERY.BUTTON_TOOLTIP),
        variant: 'icon',
        verticalModeProps: {
            helper: translateKey(WREX_IMAGE_GALLERY.BUTTON_HELPER),
        },
    };

    const insertImageGalleryDialog = (
        <ImageGalleryDialog
            isOpen={isImageGalleryDialogOpen}
            images={pickedImages}
            onClose={closeImageGalleryDialog}
            onSave={handleOnSave}
            isNewImageGallery
        />
    );

    return shouldHideButton
        ? undefined
        : {
              hiddenInput,
              insertImageGalleryButtonProps,
              insertImageGalleryDialog,
          };
};
